import { useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Store, authSlice, useDispatch, useSelector } from '../../stores';
import { fetchCompliance } from '../../stores/slices/Legal';
import useWindowDimensions from '../../utils/useWidowDimensions';
import { useLocation } from 'react-router-dom';
import { Footer } from './components/Footer';
import { FrameBars } from './components/FrameBars';
import { PixBar } from './components/PixBar';
import { PrivatePixBar } from './components/PrivatePixBar';
import { TicketBar } from './components/TicketBar';
import { IrregularitiesBar } from './components/IrregularitiesBar';
import { LGPDModal } from './modals/LGPDModal';
import { ManagePixModal } from './modals/ManagePixModal';
import { ManagePrivatePixModal } from './modals/ManagePrivatePixModal';
import { StyledContainer } from './styles';
import { IRREGULARITIES, VEHICLES } from '../../routes';

export const drawerWidth = 260;
export const ticketBarHeight = 48;

export const PageWrapper = (props: {
  children:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const { token, tokenExpiration } = useSelector((state) => state.authReducer);
  const { driverId } = useSelector((state) => state.profileReducer);
  const { ticketTimeLeft, ticketRenovate } = useSelector(
    (state) => state.ticketPurchaseReducer,
  );
  const { hasOpenPix, hasOpenPrivatePix } = useSelector(
    (state) => state.pixReducer,
  );
  const { hasAnyIrregularity } = useSelector(
    (state) => state.irregularitiesReducer,
  );
  const { browserHeight } = useWindowDimensions();
  const [paddingTopModifier, setPaddingTopModifier] = useState(0);

  const isIrregularitiesPage =
    location.pathname === IRREGULARITIES ||
    location.pathname.startsWith(`${IRREGULARITIES}/`);
  const isVehiclesPage =
    location.pathname === VEHICLES ||
    location.pathname.startsWith(`${VEHICLES}/`);
  const hideIrregularitiesBar = isIrregularitiesPage || isVehiclesPage;

  useEffect(() => {
    if (token && tokenExpiration) {
      const intervalId = setInterval(() => {
        if (moment(tokenExpiration).isBefore(moment())) {
          localStorage.removeItem('NITROT:token');
          localStorage.removeItem('NITROT:driverId');
          window.dispatchEvent(new Event('storage'));
          Store.dispatch(authSlice.actions.logout());
          window.location.reload();
        }
        return () => clearInterval(intervalId);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [token, tokenExpiration]);

  useEffect(() => {
    const hasTicket = ticketTimeLeft > 0 || ticketRenovate ? 1 : 0;
    const hasIrregularitiesBar =
      hasAnyIrregularity && !hideIrregularitiesBar ? 1 : 0;
    const hasPix = hasOpenPix ? 1 : 0;
    const hasPrivatePix = hasOpenPrivatePix ? 1 : 0;
    setPaddingTopModifier(
      hasTicket + hasIrregularitiesBar + hasPix + hasPrivatePix,
    );
  }, [
    ticketTimeLeft,
    ticketRenovate,
    hasOpenPix,
    hasOpenPrivatePix,
    hasAnyIrregularity,
    hideIrregularitiesBar,
  ]);

  useEffect(() => {
    if (token && driverId) {
      dispatch(
        fetchCompliance({
          driverId: driverId.toString(),
        }),
      );
    }
  }, [driverId, token]);

  return (
    <FrameBars drawerWidth={drawerWidth}>
      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-end',
        }}
      >
        {!hideIrregularitiesBar && <IrregularitiesBar />}
        <TicketBar />
        <PixBar />
        <PrivatePixBar />
        <StyledContainer
          disableGutters={useMediaQuery(theme.breakpoints.only('xs'))}
          sx={{
            height: browserHeight,
            paddingTop: {
              xs: `${50 + ticketBarHeight * paddingTopModifier}px`,
              sm: `${64 + ticketBarHeight * paddingTopModifier}px`,
            },
            paddingBottom: {
              xs: 0,
              md: '40px',
            },
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          {props.children}
        </StyledContainer>
      </div>
      <Footer />
      <ManagePixModal />
      <ManagePrivatePixModal />
      <LGPDModal />
    </FrameBars>
  );
};
